import type { BarcodeFormat } from '@erp/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

const BARCODE_FORMATS: BarcodeFormat[] = ['code128', 'ean13', 'ean8', 'upc_a', 'qr'];

interface NumberingValue {
  sku?: { pattern?: string; autoOnCreate?: boolean };
  barcode?: { defaultFormat?: BarcodeFormat; autoFromSku?: boolean };
}

interface SettingsListItem {
  key: string;
  value: NumberingValue | unknown;
  updatedAt: string;
}

interface SkuPreview {
  sku: string;
  pattern: string;
  seq: number;
}

const PATTERN_HAS_SEQ = /\{seq(:\d+)?\}/;

/**
 * Settings → Numbering & barcodes.
 *
 * Two things controlled here:
 *   1. The SKU pattern + counter (consumed by POST /items/sku/next).
 *   2. The default barcode format for new items (read by the item
 *      form's initial state; existing items keep whatever format
 *      they were saved with).
 *
 * Reads via GET /settings (no single-key endpoint exists yet, so we
 * filter the array client-side); writes via PUT /settings/:key with
 * key = 'items.numbering'.
 */
export function NumberingPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('items');
  const [loaded, setLoaded] = useState(false);
  const [pattern, setPattern] = useState('ITM-{seq:6}');
  const [defaultFormat, setDefaultFormat] = useState<BarcodeFormat>('code128');
  const [preview, setPreview] = useState<SkuPreview | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const [settingsRes, previewRes] = await Promise.all([
      api().get<{ items: SettingsListItem[] }>('/settings'),
      api().get<SkuPreview>('/items/sku/preview'),
    ]);
    const numbering = settingsRes.data.items.find((s) => s.key === 'items.numbering')?.value as
      | NumberingValue
      | undefined;
    if (numbering?.sku?.pattern) setPattern(numbering.sku.pattern);
    if (numbering?.barcode?.defaultFormat) setDefaultFormat(numbering.barcode.defaultFormat);
    setPreview(previewRes.data);
    setLoaded(true);
  };

  useEffect(() => {
    void reload();
  }, []);

  const patternValid = useMemo(() => PATTERN_HAS_SEQ.test(pattern), [pattern]);

  // Local preview of `pattern` against the server-known counter, so the
  // operator sees what the next SKU will look like while still editing
  // (before clicking Save). Mirrors renderPattern() server-side.
  const localPreview = useMemo(() => {
    if (!preview || !patternValid) return null;
    return pattern.replace(/\{seq(?::(\d+))?\}/g, (_m, padStr) => {
      const s = String(preview.seq);
      if (padStr === undefined) return s;
      const pad = Number(padStr);
      return s.length >= pad ? s : s.padStart(pad, '0');
    });
  }, [pattern, patternValid, preview]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!patternValid) {
      setError(t('numbering.sku.invalidPattern'));
      return;
    }
    setSaving(true);
    setError(null);
    setSavedNote(null);
    try {
      const value: NumberingValue = {
        sku: { pattern: pattern.trim() },
        barcode: { defaultFormat },
      };
      await api().put('/settings/items.numbering', { value });
      setSavedNote(t('numbering.saved'));
      await reload();
    } catch {
      setError(t('numbering.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="mb-2 text-lg font-semibold">{t('numbering.title')}</h2>
      <p className="mb-4 text-sm text-fg-muted">{t('numbering.description')}</p>

      <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
        <section>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('numbering.sku.section')}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="num-pattern" label={t('numbering.sku.pattern')}>
              <Input
                id="num-pattern"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                aria-invalid={!patternValid}
                className="font-mono"
              />
            </FormField>
            <FormField id="num-seq" label={t('numbering.sku.currentSeq')}>
              <Input
                id="num-seq"
                value={preview ? String(preview.seq) : '—'}
                readOnly
                aria-readonly
                className="cursor-not-allowed bg-bg-muted text-fg-muted font-mono"
              />
            </FormField>
          </div>
          <p className="mt-2 text-xs text-fg-muted">{t('numbering.sku.patternHelp')}</p>
          {!patternValid && (
            <p className="mt-2 text-xs text-danger" role="alert">
              {t('numbering.sku.invalidPattern')}
            </p>
          )}
          {patternValid && localPreview && (
            <p className="mt-2 text-sm">
              {t('numbering.sku.preview')}:{' '}
              <span className="font-mono font-semibold">{localPreview}</span>
            </p>
          )}
        </section>

        <section>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('numbering.barcode.section')}
          </h3>
          <FormField id="num-fmt" label={t('numbering.barcode.defaultFormat')}>
            <select
              id="num-fmt"
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={defaultFormat}
              onChange={(e) => setDefaultFormat(e.target.value as BarcodeFormat)}
            >
              {BARCODE_FORMATS.map((fmt) => (
                <option key={fmt} value={fmt}>
                  {tc(`barcodeFormat.${fmt}`)}
                </option>
              ))}
            </select>
          </FormField>
        </section>

        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        {savedNote && (
          <p className="text-sm text-success" role="status">
            {savedNote}
          </p>
        )}

        <div>
          <Button type="submit" loading={saving} disabled={!patternValid}>
            {t('numbering.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
